import { msg } from '@lingui/macro'

import * as React from 'react'

import { TFCartContent } from '~graphql/generated/clientGraphql'
import { TGroupedItem } from '~logic/helpers/groupUtils/groupItems'
import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'
import { TColorKey, TContrastLevel } from '~theme/typings'
import { CartItem, CartItemSkeleton } from '~ui/components/cart/CartItem'
import { CartItemSpecial } from '~ui/components/cart/CartItemSpecial'
import { Box, Grid, GridCentered } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Text } from '~ui/components/typography/Text/Text'
import { TSingleRadius } from '~ui/styleGuide/radiuses'
import { TSingleSpace } from '~ui/styleGuide/spaces'

import { useCartContentCtx } from '../contexts/CartContentProvider'
import { getGroupedOrderItems } from '../helpers/getGroupedOrderItems'
import { getDiscountItems, getSpecialItems } from '../helpers/getSpecialItems'
import {
  CART_CONTENT_DIVIDER_THICKNESS,
  CartContentDivider,
} from './CartContentDivider'
import { CartItemAmountControl } from './CartItemAmountControl'
import { CartTotal } from './CartTotal'

type TCartItem = TFCartContent['items'][number]

type TCartTotalVariant = 'none' | 'sticky' | 'regular'

// TODO TEditableProps

type TProps = NoChildren &
  SkeletonProps & {
    bgColor: TColorKey
    isDisabled?: boolean
    isCartEditable: boolean
    emptyCartMessage: LinguiMessageDescriptor
    /**
     * to align the empty cart message correctly
     */
    emptyCartContainerProps?: PropsOf<typeof GridCentered>
    canCreateOrder?: boolean
    totalVariant?: TCartTotalVariant
    onCartItemEdit?: (cartItem: TGroupedItem<TCartItem>) => void
    onTransferFeeClick?: () => void
    /**
     * use with totalVariant `sticky` to align the sticky total part correctly
     */
    wrapperStyles?: {
      padding: TSingleSpace
      bottomRadius: TSingleRadius
    }
    cartTotalHeight: string
    refStickyCartTotal: React.RefObject<HTMLDivElement>
    creditsForOrder?: React.ReactNode
    creditPayment?: PropsOf<typeof CartTotal>['creditPayment']
  }

export const CART_CONTENT_ROW_GAP: TSingleSpace = `medium`
const itemDividerContrast: TContrastLevel = `low`
const totalDividerContrast: TContrastLevel = `high`

/**
 * The content of the expanded/open cart (order items & summary).
 * Can be used in both desktop and mobile cart.
 */
export const CartContent: React.FC<TProps> = ({
  bgColor,
  showSkeleton = false,
  isCartEditable,
  canCreateOrder = true,
  emptyCartMessage,
  emptyCartContainerProps,
  isDisabled = false,
  onCartItemEdit,
  onTransferFeeClick,
  wrapperStyles,
  totalVariant = `regular`,
  cartTotalHeight,
  refStickyCartTotal,
  creditsForOrder,
  creditPayment,
}) => {
  const { trans } = useTrans()
  const order = useCartContentCtx()
  const cartTotalPadding = wrapperStyles?.padding ?? `none`
  const cartRadius = wrapperStyles?.bottomRadius ?? `none`

  const showMessage = () => {
    return (
      <GridCentered flexGrow={1} {...emptyCartContainerProps}>
        <Text fontSize={`m18`} contrast={`low`} textAlign={`center`} fontStyle={`boldSemi`}>
          {trans(emptyCartMessage)}
        </Text>
      </GridCentered>
    )
  }

  if (!canCreateOrder) {
    // read-only menu is rendered with empty but still visible cart
    return showMessage()
  }

  // cart is loading -> show some placeholder skeleton
  if (order === null || showSkeleton) {
    const placeholderCartItems = [1, 2, 3]

    return (
      <Grid gap={CART_CONTENT_ROW_GAP}>
        {/* ORDER ITEMS */}
        {placeholderCartItems.map((item) => (
          <CartItemSkeleton key={item} isCartEditable={isCartEditable} />
        ))}

        <CartContentDivider contrast={totalDividerContrast} />

        <CartTotal showSkeleton />
      </Grid>
    )
  }

  const cartItems = getGroupedOrderItems(order)
  const discountItems = getDiscountItems(order)(trans)
  const specialItems = getSpecialItems(order)(onTransferFeeClick)(trans)

  const cartIsEmpty = cartItems.length === 0
  const noAddonsLabel = trans(msg`no addons`)

  if (cartIsEmpty) {
    return showMessage()
  }

  return (
    <Grid gap={CART_CONTENT_ROW_GAP}>
      <Stack
        spacing={CART_CONTENT_ROW_GAP}
        dividerProps={{
          thickness: `${CART_CONTENT_DIVIDER_THICKNESS}px`,
          contrast: itemDividerContrast,
        }}
      >
        {/* ORDER ITEMS */}
        {cartItems.map((item) => (
          <CartItem
            key={item.id}
            amount={item.count}
            noAddonsLabel={noAddonsLabel}
            isCartEditable={isCartEditable}
            onEdit={() => onCartItemEdit?.(item)}
            isDisabled={isDisabled || item.firstItem.addedAutomatically}
            amountControl={isCartEditable && <CartItemAmountControl item={item} />}
            wareItem={{
              id: item.firstItem.product.id,
              price: item.firstItem.price.formattedValue,
              note: item.firstItem.note,
              nameLabel: item.firstItem.product.nameLabel,
              canHaveAddons: item.firstItem.product.availableAddonCount > 0,
              addons: item.firstItem.addons.map((addon) => ({
                amount: 1,
                id: addon.id,
                label: addon.product.nameLabel,
              })),
            }}
          />
        ))}

        {/* DISCOUNTS */}
        {discountItems.map((item) => (
          <CartItem
            key={item.key}
            amount={item.amount}
            noAddonsLabel={noAddonsLabel}
            isCartEditable={isCartEditable}
            wareItem={{
              addons: [],
              id: item.key,
              canHaveAddons: false,
              nameLabel: item.label,
              price: item.formattedValue,
            }}
          />
        ))}
      </Stack>

      {specialItems.length > 0 && <CartContentDivider />}

      {/* FEES, TIP, COVERS, ... */}
      {specialItems.map((item) => (
        <CartItemSpecial
          key={item.key}
          wareItem={{
            nameLabel: item.label,
            price: item.formattedValue,
            onClick: item.onClick,
          }}
        />
      ))}

      {totalVariant === `none` && (
        <>
          {creditsForOrder}
          <Box /> {/* to add grid gap to the end of the cart content */}
        </>
      )}

      {/* TOTAL SUM */}
      {totalVariant === `regular` && (
        <>
          <CartContentDivider contrast={totalDividerContrast} />
          <CartTotal creditPayment={creditPayment} />
          {creditsForOrder}
        </>
      )}

      {totalVariant === `sticky` && (
        <>
          <Grid
            ref={refStickyCartTotal}
            left={0}
            right={0}
            bottom={0}
            bgColor={bgColor}
            position={`absolute`}
            rowGap={cartTotalPadding}
            padding={{
              top: `none`,
              left: cartTotalPadding,
              right: cartTotalPadding,
              bottom: cartTotalPadding,
            }}
            radius={{
              topLeft: `none`,
              topRight: `none`,
              bottomLeft: cartRadius,
              bottomRight: cartRadius,
            }}
          >
            <CartContentDivider contrast={totalDividerContrast} />
            <CartTotal creditPayment={creditPayment} />
            {creditsForOrder}
          </Grid>

          {/* box to compensate absolutely positioned total price */}
          <Box height={cartTotalHeight} />
        </>
      )}
    </Grid>
  )
}
